import { Hub } from '@aws-amplify/core'
import { ApplicationAuth, useTenant } from '@emerald-works-nova/auth'
import { EventBusProvider } from '@emerald-works/react-event-bus-client'
import { GlobalStyles, useTheme } from '@mui/material'
import CssBaseline from '@mui/material/CssBaseline'
import React from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch } from 'react-redux'
import { Outlet, matchPath, useLocation } from 'react-router'
import { FreeAccountCtaModal } from './components'
import AlertProvider from './components/alert/provider'
import Favicon from './components/favicon'
import { GleapProvider } from './components/gleap'
import { GoogleAdProvider } from './components/google-ad/google-ad-context'
import { LPQProvider } from './components/lpq-provider'
import { MindsetProvider } from './components/mindset-agent/mindset-context'
import { SEOProvider } from './components/seo-context'
import consts from './consts'
import ErrorBoundary from './components/error-boundary'
import { useCleanCookies } from './hooks'
import { EventContextProvider } from './hooks/useEventContext'
import usePageTracking from './hooks/useGA'
import useSSOSessionExpires from './hooks/useSSOSessionExpires'
import useSessionInactivity from './hooks/useSessionInactivity'
import { authErrorSlice } from './reducers'
import { userCookiesGenerated } from './reducers/user/event-bus'
import { routes } from './router/routes'

const SessionInactivity = () => {
  // Handle regular session inactivity (logs out non-SSO users)
  useSessionInactivity()

  // Handle SSO session expiration (shows modal for SSO users)
  const ssoModalComponent = useSSOSessionExpires()

  return ssoModalComponent
}

/**
 * Known routes that are NOT content/article pages
 *
 * Content pages use the pattern /:locationId/* where the locationId is not in this list.
 * This list helps distinguish between:
 * - Application pages (home, accelerate, search, etc.) -> Use off-white background
 * - Content/article pages (URLs starting with /a[locationId]/) -> Use white background
 * - Category landing pages (URLs starting with /c[locationId]/) -> Use off-white background
 */
const KNOWN_NON_CONTENT_ROUTES = [
  '/home', '/accelerate', '/msb', '/bookmarks', '/my-learning', '/playlists',
  '/skill-bites', '/search-results', '/profile', '/signup', '/signin', '/signout', '/login',
  '/assessments', '/authors', '/categories', '/newest-releases', '/most-popular',
  '/admin', '/password-recovery', '/password-reset', '/password-recovered', '/verify-email',
  '/change-password', '/policies', '/cornerstone', '/b2c', '/social-signup',
  '/migration', '/not-found', '/bonus-material', '/maintenance'
]

const App = () => {
  const tenant = useTenant()
  const dispatch = useDispatch()
  const location = useLocation()
  const theme = useTheme()
  const prevPathname = React.useRef(location.pathname)

  React.useEffect(() => {
    // Only scroll to top when the pathname actually changes (different route)
    // This preserves scroll position when only query parameters change (filtering, tabs, search)
    // Each page can implement its own scroll behavior for specific actions (like pagination)
    if (prevPathname.current !== location.pathname) {
      window.scrollTo(0, 0)
      prevPathname.current = location.pathname
    }
  }, [location.pathname])

  usePageTracking()
  useCleanCookies()

  /**
   * Determine if current route is a content/article page
   *
   * URL Pattern Detection:
   * - Content/Article pages: /a[locationId]/article-title (e.g., /a123456/sustainable-procurement)
   * - Category landing pages: /c[locationId]/category-name (e.g., /c789012/leadership)
   * - Known app routes: /home, /accelerate, /search-results, etc.
   *
   * Background Color Logic:
   * - Article pages (starts with 'a') -> Pure white (#FFF) for reading experience
   * - Category pages (starts with 'c') -> Off-white (#FBFBFB) to separate content cards
   * - All other pages -> Off-white (#FBFBFB) for visual hierarchy
   */
  const isContentPage = React.useMemo(() => {
    const pathname = location.pathname

    // First, check if it's a known application route
    const isKnownRoute = KNOWN_NON_CONTENT_ROUTES.some(route => pathname.startsWith(route))
    if (isKnownRoute) return false

    // Check if URL matches the dynamic /:locationId/* pattern
    const match = matchPath({ path: '/:locationId/*' }, pathname)
    if (!match) return false

    // Extract locationId and check its first character
    // 'a' prefix = article/content page (white background)
    // 'c' prefix = category landing page (off-white background)
    const locationId = match.params.locationId
    return locationId && locationId[0] === 'a'
  }, [location.pathname])

  // Apply background colors based on page type
  // Pure white for articles (better reading experience), off-white for everything else
  const bodyBackgroundColor = isContentPage ? theme.palette.white.main : theme.palette.white.off

  const trackEventBusState = (eventName, data) => {
    if (window.DD_RUM && typeof window.DD_RUM.addAction === 'function') {
      window.DD_RUM.addAction(eventName, data)
    } else {
      console.log('addAction is not available')
    }
  }

  // capture auth channel from @amplify/auth HUB and dispatch it to the store
  Hub.listen('auth', (data) => {
    // capture only signIn_failure events for now
    if (data?.payload?.event !== 'signIn_failure') return
    const { data: { message: errorMessage }, event, message } = data.payload
    dispatch(authErrorSlice.actions.setAuthError({ errorMessage, event, message }))
  })

  return (
    <ErrorBoundary>
      <Helmet>
        <meta name='prerender-status-code' content='200' />
      </Helmet>
      <ApplicationAuth
        loginRoute={routes.login.path}
        tenant={tenant}
        authOptions={{ authenticationFlowType: 'USER_PASSWORD_AUTH' }}
      >
        <EventBusProvider
          eventBusURL={tenant.eventBusUrl || process.env.REACT_APP_EVENT_BUS_URL}
          connectionParams={{
            tenantKey: tenant?.tenantKey || consts.DEFAULT_CLUB_TENANT_KEY
          }}
          initialisers={[
            userCookiesGenerated
          ]}
          options={{
            socketIoUrl: tenant.eventBusBackupURL || (process.env.REACT_APP_EVENT_BUS_BACKUP_URL || 'https://socketio.ewnova.dev')
          }}
          useAuthProvider
          fetchUserProfile
          namespace='toolkit'
          onConnectionChange={status => trackEventBusState('onConnectionChange', status)}
          onOpen={data => trackEventBusState('onOpen', data)}
          onClose={data => trackEventBusState('onClose', data)}
          onReconnect={data => trackEventBusState('onReconnect', data)}
          onMaximum={data => trackEventBusState('onMaximum', data)}
          onError={data => trackEventBusState('onError', data)}
        >
          <EventContextProvider>
            <GoogleAdProvider>
              <SEOProvider>
                <MindsetProvider>
                  <LPQProvider>
                    <AlertProvider>
                      <GleapProvider>
                        <Favicon />
                        <CssBaseline />
                        <GlobalStyles
                          styles={{
                            body: {
                              backgroundColor: bodyBackgroundColor
                            }
                          }}
                        />
                        <FreeAccountCtaModal />
                        <SessionInactivity />
                        <Outlet />
                      </GleapProvider>
                    </AlertProvider>
                  </LPQProvider>
                </MindsetProvider>
              </SEOProvider>
            </GoogleAdProvider>
          </EventContextProvider>
        </EventBusProvider>
      </ApplicationAuth>
    </ErrorBoundary>
  )
}
export default App
